"use client";

import { useEffect, useRef } from "react";
import type LenisType from "lenis";

// Expose Lenis instance globally so other components (e.g. ScrollToTop)
// can call `.scrollTo()` without spawning a competing native smooth-scroll.
declare global {
  interface Window {
    __lenis?: LenisType;
  }
}

/**
 * Lenis smooth scroll provider.
 * Integrates with GSAP ScrollTrigger for frame-perfect sync.
 *
 * Scroll feel tuning (tightened to cut the floaty "sway" without going fully
 * native — keeps a hint of smoothing but tracks the input closely):
 *   - lerp: 0.4 (frame-rate-independent smoothing. 0.14 settled ~800ms after
 *     input stopped — long enough to read as the page "ghost scrolling" on
 *     its own after the user let go. 0.4 settles in under ~150ms: still a
 *     touch smoother than native, but the glide is no longer perceptible.
 *     Lenis ignores duration/easing when lerp is set, so we use lerp only.)
 *   - touchMultiplier: 1.2 (was 1.8 — less overshoot on mobile)
 *   - wheelMultiplier: 1.0 (1:1 with mouse wheel)
 *
 * Respects prefers-reduced-motion — falls back to native scroll.
 */
export default function SmoothScroll({ children }: { children: React.ReactNode }) {
  const lenisRef = useRef<LenisType | null>(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let cleanup: (() => void) | undefined;
    let cancelled = false;

    // Dynamic-import Lenis + GSAP so they stay out of the critical bundle.
    // Adds one tick of latency to smooth-scroll activation — imperceptible to users.
    const ric = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    const init = () => {
      Promise.all([import("lenis"), import("@/lib/gsap")]).then(
        ([{ default: Lenis }, { gsap, ScrollTrigger }]) => {
          if (cancelled) return;

          const lenis = new Lenis({
            lerp: 0.4,
            wheelMultiplier: 1.0,
            touchMultiplier: 1.2,
            infinite: false,
          });

          lenisRef.current = lenis;
          window.__lenis = lenis;

          lenis.on("scroll", ScrollTrigger.update);

          const tickHandler = (time: number) => {
            lenis.raf(time * 1000);
          };

          gsap.ticker.add(tickHandler);
          gsap.ticker.lagSmoothing(500, 33);

          cleanup = () => {
            gsap.ticker.remove(tickHandler);
            lenis.destroy();
            lenisRef.current = null;
            if (window.__lenis === lenis) window.__lenis = undefined;
          };
        },
      );
    };

    if (ric) ric(init, { timeout: 3000 });
    else setTimeout(init, 3000);

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, []);

  return <>{children}</>;
}
